if (!customElements.get('gift-box-builder')) {
  class GiftBoxBuilder extends HTMLElement {
    constructor() {
      super();
      this.formatMoney = this.formatMoney.bind(this);
    }

    connectedCallback() {
      this.packagingCost = parseInt(this.dataset.packagingCost) || 0;
      this.totalSlots = parseInt(this.dataset.totalSlots) || 1;
      this.productFormId = this.dataset.productFormId;
      this.errorMessage = this.querySelector('.gbb-error-message');
      this.slots = new Array(this.totalSlots).fill(null);
      this.slotMeta = [];
      this.breakdownExpanded = true;
      this.activeVariantTitle = '';

      this.parentForm = document.getElementById(this.productFormId) || this.closest('form');
      
      // Collect slot metadata from markup
      this.querySelectorAll('.gbb-slot-row').forEach(row => {
        const slotIndex = parseInt(row.dataset.slotIndex);
        const capacity = row.dataset.slotCapacity || '';
        const nameEl = row.querySelector('.gbb-slot-name');
        const numberEl = row.querySelector('.gbb-slot-number');
        
        this.slotMeta[slotIndex] = {
          index: slotIndex,
          capacity: capacity,
          name: nameEl ? nameEl.textContent.trim() : (capacity ? `${capacity}g Jar` : `Container ${slotIndex + 1}`),
          numberText: numberEl ? numberEl.textContent.trim() : `Container ${slotIndex + 1}`
        };
      });

      // Bind click on custom dropdown toggle buttons
      this.querySelectorAll('.gbb-select-toggle').forEach(btn => {
        btn.addEventListener('click', this.handleToggleDropdown.bind(this));
      });

      // Bind click on custom dropdown items
      this.querySelectorAll('.gbb-dropdown-item').forEach(item => {
        item.addEventListener('click', this.handleItemSelect.bind(this));
      });

      // Close dropdowns when clicking outside
      document.addEventListener('click', this.handleOutsideClick.bind(this));

      // Retrieve initial variant title
      const section = this.closest('section');
      const sectionId = section?.id.replace('shopify-section-', '') || '';
      const jsonBlock = document.getElementById(`ProductJSON-${sectionId}`) || document.querySelector('[id^="ProductJSON-"]');
      const idInput = this.parentForm ? this.parentForm.querySelector('input[name="id"]') : document.querySelector(`input[name="id"][form="${this.productFormId}"]`);
      const currentVariantId = idInput?.value;

      if (jsonBlock && currentVariantId) {
        try {
          const productData = JSON.parse(jsonBlock.textContent);
          const activeVariant = productData.variants.find(v => v.id == currentVariantId);
          if (activeVariant && activeVariant.title && !activeVariant.title.toLowerCase().includes('default title')) {
            this.activeVariantTitle = activeVariant.title;
          }
        } catch (e) {
          console.error("Failed to parse initial product JSON:", e);
        }
      }

      if (this.parentForm) {
        // Intercept form submissions
        this.parentForm.addEventListener('submit', this.validateAndSubmit.bind(this), { capture: true });
      }

      // Listen to variant changes on the main box product
      if (idInput) {
        idInput.addEventListener('change', this.handleBaseVariantChange.bind(this));
      }

      // Also listen to variant options / radios / selects in the section
      document.querySelectorAll(`input[name="id"][form="${this.productFormId}"], .product-variant--item input, .select-option-box select`).forEach(input => {
        input.addEventListener('change', () => {
          setTimeout(() => {
            const currIdInput = this.parentForm ? this.parentForm.querySelector('input[name="id"]') : document.querySelector(`input[name="id"][form="${this.productFormId}"]`);
            if (currIdInput && currIdInput.value) {
              this.handleBaseVariantChange({ target: currIdInput });
            }
          }, 80);
        });
      });

      // Listen to quantity adjustments
      this.quantityInput = this.parentForm ? this.parentForm.querySelector('input[name="quantity"]') : document.querySelector(`input[name="quantity"][form="${this.productFormId}"]`);
      if (!this.quantityInput) {
        this.quantityInput = document.querySelector('.quantity-input') || document.querySelector('input[name="quantity"]');
      }
      if (this.quantityInput) {
        this.quantityInput.addEventListener('change', () => this.recalculateTotalPrice());
        this.quantityInput.addEventListener('input', () => this.recalculateTotalPrice());
      }

      // Listen to quantity plus / minus buttons
      document.querySelectorAll('[data-decrement-quantity], [data-increment-quantity], .quantity-minus, .quantity-plus').forEach(btn => {
        btn.addEventListener('click', () => {
          setTimeout(() => this.recalculateTotalPrice(), 60);
        });
      });

      // Observe price container mutations from theme variant re-renders
      const priceDestination = document.getElementById(`product-price-list-${sectionId}`) || document.querySelector('.main-product-wrapper .product-price-list');
      if (priceDestination && window.MutationObserver) {
        this.priceObserver = new MutationObserver(() => {
          if (this._isInternalPriceUpdate) return;
          const hasBreakdown = priceDestination.querySelector('.gbb-price-breakdown');
          if (!hasBreakdown) {
            this.recalculateTotalPrice();
          }
        });
        this.priceObserver.observe(priceDestination, { childList: true });
      }

      // Parse initial state for pre-selected items on load
      this.querySelectorAll('.gbb-slot-row').forEach(row => {
        const slotIndex = parseInt(row.dataset.slotIndex);
        const customSelect = row.querySelector('.gbb-custom-select');
        const selectedId = customSelect ? customSelect.dataset.selectedId : '';
        
        if (selectedId) {
          const toggleBtn = customSelect.querySelector('.gbb-select-toggle');
          const selectedWrapper = toggleBtn.querySelector('.gbb-toggle-selected');
          const title = selectedWrapper.querySelector('.gbb-toggle-title').textContent.trim();
          
          const preselectedItem = customSelect.querySelector(`.gbb-dropdown-item[data-value="${selectedId}"]`);
          if (preselectedItem) {
            const isChocolate = preselectedItem.dataset.isChocolate === 'true';
            const capacity = parseInt(row.dataset.slotCapacity) || 0;
            
            let variants = [];
            try {
              variants = JSON.parse(preselectedItem.dataset.variants || '[]');
            } catch (e) {
              console.error("Failed to parse variants JSON array from item:", e);
            }
            
            let matchedVariant = variants.find(v => v.title.includes(capacity.toString()));
            if (!matchedVariant && variants.length > 0) {
              matchedVariant = variants[0];
            }
            
            const variantId = matchedVariant ? matchedVariant.id : '';
            const calculatedPrice = matchedVariant ? matchedVariant.price : 0;
            
            this.slots[slotIndex] = {
              productId: selectedId,
              variantId,
              title,
              price: calculatedPrice,
              isChocolate
            };
          }
        }
      });

      this.recalculateTotalPrice();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleOutsideClick.bind(this));
      if (this.priceObserver) {
        this.priceObserver.disconnect();
      }
    }

    handleBaseVariantChange(event) {
      const section = this.closest('section');
      const sectionId = section?.id.replace('shopify-section-', '') || '';
      const jsonBlock = document.getElementById(`ProductJSON-${sectionId}`) || document.querySelector('[id^="ProductJSON-"]');
      
      if (jsonBlock) {
        try {
          const productData = JSON.parse(jsonBlock.textContent);
          const activeVariant = productData.variants.find(v => v.id == event.target.value);
          if (activeVariant) {
            this.packagingCost = activeVariant.price;
            if (activeVariant.title && !activeVariant.title.toLowerCase().includes('default title')) {
              this.activeVariantTitle = activeVariant.title;
            } else {
              this.activeVariantTitle = '';
            }
            this.recalculateTotalPrice();
            
            // Re-trigger calculation after timeouts to override theme's default AJAX/DOM replacement changes
            setTimeout(() => {
              this.recalculateTotalPrice();
            }, 100);
            setTimeout(() => {
              this.recalculateTotalPrice();
            }, 350);
          }
        } catch (e) {
          console.error("Failed to parse product JSON for variant change:", e);
        }
      }
    }

    handleToggleDropdown(event) {
      event.preventDefault();
      event.stopPropagation();
      
      const btn = event.currentTarget;
      const customSelect = btn.closest('.gbb-custom-select');
      const dropdown = customSelect.querySelector('.gbb-dropdown-list');
      
      const isExpanded = btn.getAttribute('aria-expanded') === 'true';

      // Close all other dropdowns
      this.closeAllDropdowns();

      if (!isExpanded) {
        btn.setAttribute('aria-expanded', 'true');
        customSelect.classList.add('active');
        dropdown.classList.remove('hidden');
      }
    }

    handleOutsideClick(event) {
      // Close dropdowns if click is outside of custom select elements
      if (!event.target.closest('.gbb-custom-select')) {
        this.closeAllDropdowns();
      }
    }

    closeAllDropdowns() {
      this.querySelectorAll('.gbb-select-toggle').forEach(btn => {
        btn.setAttribute('aria-expanded', 'false');
      });
      this.querySelectorAll('.gbb-custom-select').forEach(el => {
        el.classList.remove('active');
      });
      this.querySelectorAll('.gbb-dropdown-list').forEach(list => {
        list.classList.add('hidden');
      });
    }

    handleItemSelect(event) {
      event.preventDefault();
      event.stopPropagation();

      const item = event.currentTarget;
      
      // Do nothing if product is sold out
      if (item.dataset.isSoldOut === 'true') return;

      const customSelect = item.closest('.gbb-custom-select');
      const toggleBtn = customSelect.querySelector('.gbb-select-toggle');
      const slotRow = item.closest('.gbb-slot-row');
      const slotIndex = parseInt(slotRow.dataset.slotIndex);
      const capacity = parseInt(slotRow.dataset.slotCapacity) || 0;
      
      const productId = item.dataset.value;

      if (!productId) {
        // Clear selection
        this.slots[slotIndex] = null;
        customSelect.dataset.selectedId = '';
        
        // Update Toggle UI to placeholder state
        toggleBtn.querySelector('.gbb-toggle-placeholder').classList.remove('hidden');
        toggleBtn.querySelector('.gbb-toggle-selected').classList.add('hidden');
        
        // Update slot price display
        slotRow.querySelector('.gbb-slot-row-price').textContent = '₹0.00';
        
        this.closeAllDropdowns();
        this.clearError();
        this.recalculateTotalPrice();
        return;
      }

      const title = item.dataset.title;
      const imageUrl = item.dataset.image;
      const isChocolate = item.dataset.isChocolate === 'true';

      // Enforce chocolate can only be added once in the gift box
      if (isChocolate) {
        const alreadyHasChocolate = this.slots.some((s, idx) => s && s.isChocolate && idx !== slotIndex);
        if (alreadyHasChocolate) {
          this.showError("Chocolate can only be added once in the gift box.");
          this.closeAllDropdowns();
          return;
        }
      }

      // Extract variant matching slot capacity from serialized variant data
      let variants = [];
      try {
        variants = JSON.parse(item.dataset.variants || '[]');
      } catch (e) {
        console.error("Failed to parse variants JSON array from item:", e);
      }

      // Try matching variant title by capacity number
      let matchedVariant = variants.find(v => v.title.includes(capacity.toString()));
      if (!matchedVariant && variants.length > 0) {
        matchedVariant = variants[0];
      }

      const variantId = matchedVariant ? matchedVariant.id : '';
      const calculatedPrice = matchedVariant ? matchedVariant.price : 0;

      // Log matched variant details in development mode
      const isDev = window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost' || window.Shopify?.designMode;
      if (isDev) {
        console.log(
          `Gift Box Component:\n${title}\n` +
          `Container:\n${capacity}g\n` +
          `Matched Variant:\n${matchedVariant ? matchedVariant.title : 'None'}\n` +
          `Variant ID:\n${variantId}\n` +
          `Price:\n${calculatedPrice}`
        );
      }

      // Set slot state
      this.slots[slotIndex] = {
        productId,
        variantId,
        title,
        price: calculatedPrice,
        isChocolate
      };
      
      customSelect.dataset.selectedId = productId;

      // Update Toggle UI to selected state
      const selectedWrapper = toggleBtn.querySelector('.gbb-toggle-selected');
      const toggleImg = selectedWrapper.querySelector('.gbb-toggle-image');
      
      if (imageUrl) {
        toggleImg.src = imageUrl;
        toggleImg.classList.remove('hidden');
      } else {
        toggleImg.src = '';
        toggleImg.classList.add('hidden');
      }

      selectedWrapper.querySelector('.gbb-toggle-title').textContent = title;
      
      toggleBtn.querySelector('.gbb-toggle-placeholder').classList.add('hidden');
      selectedWrapper.classList.remove('hidden');

      // Update slot row price display
      slotRow.querySelector('.gbb-slot-row-price').textContent = this.formatMoney(calculatedPrice);

      this.closeAllDropdowns();
      this.clearError();
      this.recalculateTotalPrice();
    }

    getQuantity() {
      const input = this.quantityInput || (this.parentForm ? this.parentForm.querySelector('input[name="quantity"]') : null) || document.querySelector(`input[name="quantity"][form="${this.productFormId}"]`) || document.querySelector('.quantity-input') || document.querySelector('input[name="quantity"]');
      if (input) {
        const val = parseInt(input.value);
        return !isNaN(val) && val > 0 ? val : 1;
      }
      return 1;
    }

    recalculateTotalPrice() {
      let contentsCost = 0;
      this.slots.forEach(slot => {
        if (slot) contentsCost += slot.price;
      });

      const qty = this.getQuantity();
      const unitTotal = this.packagingCost + contentsCost;
      const totalCents = unitTotal * qty;
      this.updatePriceDisplay(totalCents, unitTotal, qty);
    }

    updatePriceDisplay(totalCents, unitTotal, qty) {
      this._isInternalPriceUpdate = true;
      const formattedTotal = this.formatMoney(totalCents);

      const section = this.closest('section');
      const sectionId = section?.id.replace('shopify-section-', '') || '';

      // Find all main product price containers on the page
      const mainPriceLists = document.querySelectorAll(
        `.main-product-wrapper .product-price-list, [data-main-price] .product-price-list, #price-${sectionId} .product-price-list, #product-price-list-${sectionId}`
      );
      const targetLists = mainPriceLists.length > 0 ? Array.from(new Set(mainPriceLists)) : Array.from(document.querySelectorAll('.product-price-list'));

      targetLists.forEach(priceList => {
        let salePriceEl = priceList.querySelector('.product-price-actual');
        if (!salePriceEl) {
          salePriceEl = document.createElement('product-sale-price');
          salePriceEl.className = 'product-price-actual';
          salePriceEl.innerHTML = `<span class="product-actual-price">${formattedTotal}</span>`;
          priceList.prepend(salePriceEl);
        } else {
          let actualPriceEl = salePriceEl.querySelector('.product-actual-price, .product-actual-price-1');
          if (actualPriceEl) {
            actualPriceEl.textContent = formattedTotal;
          } else {
            const newSpan = document.createElement('span');
            newSpan.className = 'product-actual-price';
            newSpan.textContent = formattedTotal;
            salePriceEl.prepend(newSpan);
          }
        }

        // Hide base compare price and discount badge once custom items are added
        const comparePriceEl = salePriceEl.querySelector('.product-compare-price');
        const discountBadge = salePriceEl.querySelector('.custom-discount-badge');
        if (comparePriceEl) {
          if (this.slots.some(Boolean)) {
            comparePriceEl.style.display = 'none';
            if (discountBadge) discountBadge.style.display = 'none';
          } else {
            comparePriceEl.style.display = '';
            if (discountBadge) discountBadge.style.display = '';
          }
        }

        // Render / update price breakdown right below product-actual-price
        let breakdownEl = priceList.querySelector('.gbb-price-breakdown');
        if (!breakdownEl) {
          breakdownEl = document.createElement('div');
          breakdownEl.className = 'gbb-price-breakdown';
          salePriceEl.insertAdjacentElement('afterend', breakdownEl);
        }

        breakdownEl.innerHTML = this.renderBreakdownHTML(totalCents, unitTotal, qty);
        this.bindBreakdownEvents(breakdownEl);
      });

      // Update sticky bar or recommendation prices if present
      document.querySelectorAll('.sticky-bar-product-info .product-actual-price, .sticky-product-content .product-actual-price').forEach(el => {
        el.textContent = formattedTotal;
      });

      // Update price inside the Add to Cart button
      const buttonPrice = document.querySelector('.product-price-main') || document.querySelector('[data-add-to-cart-text] .product-price-main');
      if (buttonPrice) {
        buttonPrice.textContent = formattedTotal;
      }

      setTimeout(() => {
        this._isInternalPriceUpdate = false;
      }, 50);
    }

    renderBreakdownHTML(totalCents, unitTotal, qty) {
      const filledCount = this.slots.filter(Boolean).length;
      const isAllFilled = filledCount === this.totalSlots;
      const isExpanded = this.breakdownExpanded !== false;
      
      let baseTitle = 'Base Gift Box';
      if (this.activeVariantTitle) {
        baseTitle = `Base Box (${this.activeVariantTitle})`;
      }

      let slotsHtml = '';
      for (let i = 0; i < this.totalSlots; i++) {
        const slot = this.slots[i];
        const meta = this.slotMeta[i] || {
          name: 'Container ' + (i + 1),
          numberText: 'Container ' + (i + 1),
          capacity: ''
        };

        if (slot) {
          slotsHtml += `
            <div class="gbb-breakdown-row gbb-breakdown-slot is-filled" data-slot-target="${i}" role="button" tabindex="0" title="Click to view in builder">
              <div class="gbb-breakdown-item-info">
                <span class="gbb-breakdown-dot filled"></span>
                <div class="gbb-breakdown-text-group">
                  <span class="gbb-breakdown-item-title">${meta.numberText}: <strong>${slot.title}</strong></span>
                  <span class="gbb-breakdown-item-sub">${meta.name}${slot.isChocolate ? ' • Chocolate' : ''}</span>
                </div>
              </div>
              <span class="gbb-breakdown-price">+${this.formatMoney(slot.price)}</span>
            </div>
          `;
        } else {
          slotsHtml += `
            <div class="gbb-breakdown-row gbb-breakdown-slot is-empty" data-slot-target="${i}" role="button" tabindex="0" title="Click to select content">
              <div class="gbb-breakdown-item-info">
                <span class="gbb-breakdown-dot empty"></span>
                <div class="gbb-breakdown-text-group">
                  <span class="gbb-breakdown-item-title gbb-empty-title">${meta.numberText}: <em>Select Content</em></span>
                  <span class="gbb-breakdown-item-sub">${meta.name}</span>
                </div>
              </div>
              <span class="gbb-breakdown-price gbb-empty-price">+₹0.00</span>
            </div>
          `;
        }
      }

      const basePackagingHtml = `
        <div class="gbb-breakdown-row gbb-breakdown-base">
          <div class="gbb-breakdown-item-info">
            <span class="gbb-breakdown-dot base"></span>
            <div class="gbb-breakdown-text-group">
              <span class="gbb-breakdown-item-title"><strong>${baseTitle}</strong></span>
              <span class="gbb-breakdown-item-sub">Custom presentation box & packaging</span>
            </div>
          </div>
          <span class="gbb-breakdown-price">${this.formatMoney(this.packagingCost)}</span>
        </div>
      `;

      let quantitySummaryHtml = '';
      if (qty > 1) {
        quantitySummaryHtml = `
          <div class="gbb-breakdown-qty-summary">
            <div class="gbb-breakdown-qty-row">
              <span>Cost Per Box:</span>
              <span>${this.formatMoney(unitTotal)}</span>
            </div>
            <div class="gbb-breakdown-qty-row">
              <span>Quantity:</span>
              <span>× ${qty}</span>
            </div>
            <div class="gbb-breakdown-qty-row gbb-total-calc">
              <span>Total Customized Price:</span>
              <span class="gbb-bold-total">${this.formatMoney(totalCents)}</span>
            </div>
          </div>
        `;
      }

      const badgeText = isAllFilled ? 'All Selected' : `${filledCount}/${this.totalSlots} Selected`;
      const badgeClass = isAllFilled ? 'is-complete' : 'is-incomplete';

      return `
        <div class="gbb-breakdown-card ${isExpanded ? 'is-open' : 'is-collapsed'}">
          <button type="button" class="gbb-breakdown-header-toggle" aria-expanded="${isExpanded}">
            <div class="gbb-breakdown-header-left">
              <svg class="gbb-breakdown-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="8" y1="6" x2="21" y2="6"></line>
                <line x1="8" y1="12" x2="21" y2="12"></line>
                <line x1="8" y1="18" x2="21" y2="18"></line>
                <line x1="3" y1="6" x2="3.01" y2="6"></line>
                <line x1="3" y1="12" x2="3.01" y2="12"></line>
                <line x1="3" y1="18" x2="3.01" y2="18"></line>
              </svg>
              <span class="gbb-breakdown-header-title">Price Breakdown</span>
              <span class="gbb-breakdown-badge ${badgeClass}">${badgeText}</span>
            </div>
            <svg class="gbb-breakdown-chevron ${isExpanded ? 'rotated' : ''}" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          <div class="gbb-breakdown-body ${isExpanded ? '' : 'hidden'}">
            <div class="gbb-breakdown-list">
              ${basePackagingHtml}
              ${slotsHtml}
            </div>
            ${quantitySummaryHtml}
            <div class="gbb-breakdown-footer-status ${isAllFilled ? 'status-complete' : 'status-pending'}">
              ${isAllFilled 
                ? '<span>✓ All containers customized & ready to order</span>' 
                : `<span>Select contents for remaining ${this.totalSlots - filledCount} container(s)</span>`
              }
            </div>
          </div>
        </div>
      `;
    }

    bindBreakdownEvents(breakdownEl) {
      const toggleBtn = breakdownEl.querySelector('.gbb-breakdown-header-toggle');
      if (toggleBtn) {
        toggleBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.breakdownExpanded = !this.breakdownExpanded;
          this.recalculateTotalPrice();
        });
      }

      // Allow clicking on any container row to jump directly to that container slot in the builder
      breakdownEl.querySelectorAll('.gbb-breakdown-slot').forEach(row => {
        const handleTargetClick = (e) => {
          e.preventDefault();
          const targetIndex = parseInt(row.dataset.slotTarget);
          if (isNaN(targetIndex)) return;

          const targetSlotRow = this.querySelector(`.gbb-slot-row[data-slot-index="${targetIndex}"]`);
          if (targetSlotRow) {
            targetSlotRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Add pulse highlight
            targetSlotRow.classList.add('gbb-slot-highlight');
            setTimeout(() => {
              targetSlotRow.classList.remove('gbb-slot-highlight');
            }, 1500);

            // If empty, automatically open the dropdown
            if (!this.slots[targetIndex]) {
              const selectToggle = targetSlotRow.querySelector('.gbb-select-toggle');
              if (selectToggle && selectToggle.getAttribute('aria-expanded') !== 'true') {
                setTimeout(() => {
                  selectToggle.click();
                }, 300);
              }
            }
          }
        };

        row.addEventListener('click', handleTargetClick);
        row.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleTargetClick(e);
          }
        });
      });
    }

    validateAndSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      // Check if all slots are filled
      const emptySlotIndex = this.slots.findIndex(s => s === null);
      if (emptySlotIndex !== -1) {
        this.showError(`Please select contents for Container ${emptySlotIndex + 1}.`);
        
        // Reset ATC loading animations
        const submitBtn = this.parentForm.querySelector('[type="submit"]');
        if (submitBtn) {
          submitBtn.classList.remove('loading');
          submitBtn.removeAttribute('disabled');
          submitBtn.querySelector('.loading--spinner')?.classList.add('hidden');
          submitBtn.querySelector('[data-add-to-cart-text]')?.classList.remove('hidden');
        }

        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      this.submitGiftBox();
    }

    submitGiftBox() {
      const submitBtn = this.parentForm.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.setAttribute('disabled', true);
        submitBtn.classList.add('loading');
        this.querySelector('.loading--spinner')?.classList.remove('hidden');
      }

      const boxId = 'box_' + Date.now();
      const items = [];

      const boxVariantId = this.parentForm.querySelector('input[name="id"]').value;
      const boxQty = parseInt(this.parentForm.querySelector('input[name="quantity"]')?.value) || 1;

      // Add selected jar items first
      this.slots.forEach((slot, index) => {
        if (slot) {
          items.push({
            id: slot.variantId,
            quantity: boxQty, // Quantity matches box quantity
            properties: {
              _box_parent: boxId,
              'Box Container': `Container ${index + 1}`
            }
          });
        }
      });

      // Add Box Base Variant last (so it renders on top of the list!)
      items.push({
        id: boxVariantId,
        quantity: boxQty,
        properties: {
          _box_id: boxId,
          'Gift Box': 'Custom'
        }
      });

      // Grab dynamic cart-drawer render references if drawer is enabled
      const cartElement = document.querySelector('cart-drawer');
      const sections = cartElement ? cartElement.getSectionsToRender().map(s => s.id) : [];

      const body = {
        items: items,
        sections: sections,
        sections_url: window.location.pathname
      };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
      .then(res => res.json())
      .then(response => {
        if (response.status) {
          this.showError(response.description || response.message);
          return;
        }

        // Successfully added box, reload cart drawer or redirect
        if (cartElement) {
          cartElement.renderContents(response);
          cartElement.classList.add("is-visible");
          document.body.classList.add("overflow-hidden");
        } else {
          window.location = '/cart';
        }
      })
      .catch(e => {
        console.error("Failed to add gift box bundle to cart:", e);
        this.showError("An error occurred. Please try again.");
      })
      .finally(() => {
        if (submitBtn) {
          submitBtn.classList.remove('loading');
          submitBtn.removeAttribute('disabled');
          this.querySelector('.loading--spinner')?.classList.add('hidden');
        }
      });
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        let formatted = Shopify.formatMoney(cents, Shopify.money_format || window.Shopify.money_format);
        if (formatted.includes('$')) {
          formatted = formatted.replace('$', '₹');
        }
        return formatted;
      }
      return `₹${(cents / 100).toFixed(2)}`;
    }

    showError(msg) {
      if (this.errorMessage) {
        this.errorMessage.textContent = msg;
        this.errorMessage.classList.remove('hidden');
      }
    }

    clearError() {
      if (this.errorMessage) {
        this.errorMessage.textContent = '';
        this.errorMessage.classList.add('hidden');
      }
    }
  }

  customElements.define('gift-box-builder', GiftBoxBuilder);
}
